import type { ApolloCache } from '@apollo/client';
import type { NodeUserContext } from '@aurora/shared-generated/types/graphql-schema-types';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import type {
  BoardPoliciesFragment,
  CategoryPoliciesFragment,
  ContextMessageFragment,
  ContextNodeFragment,
  GroupHubDescendantsNodeFragment,
  GroupHubDetailsQuery,
  GroupHubPoliciesFragment,
  MessagePoliciesQuery,
  NodePoliciesQuery,
  NodeViewFragment,
  FollowMessageFragment
} from '../../types/graphql-types';

/**
 * Returns the result of the canSubscribe policy check based on the node type
 *
 * @param policiesData The node policies data
 * @param contextNode The context node to validate against
 */
function canSubscribeToNode(
  policiesData: NodePoliciesQuery,
  contextNode: ContextNodeFragment | NodeViewFragment
): boolean {
  const { coreNode: policies } = policiesData || {};
  switch (contextNode.nodeType) {
    case NodeType.BOARD: {
      return (policies as BoardPoliciesFragment)?.boardPolicies.canSubscribe.failureReason === null;
    }
    case NodeType.CATEGORY: {
      return (
        (policies as CategoryPoliciesFragment)?.categoryPolicies.canSubscribe.failureReason === null
      );
    }
    case NodeType.GROUPHUB: {
      return (
        (policies as GroupHubPoliciesFragment)?.grouphubPolicies.canSubscribe.failureReason === null
      );
    }
    default: {
      return false;
    }
  }
}

/**
 * Returns the result of the canSubscribe policy check for a message
 *
 * @param policiesData The node policies data
 */
function canSubscribeToMessage(policiesData: MessagePoliciesQuery): boolean {
  const { message } = policiesData || {};
  return message?.messagePolicies?.canSubscribe.failureReason === null;
}

/**
 * Updates follows field in cache
 *
 * @param cache The apollo cache
 */
function updateFollowsCacheAfterSubscription(cache: ApolloCache<{}>): void {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      follows: ({ DELETE }) => {
        return DELETE;
      }
    }
  });
}

/**
 * Updates a cached node or message subscription related fields with new values
 *
 * @param cache The apollo cache
 * @param contextEntity The context entity to update
 * @param isSubscribed true if user has subscribed to the entity
 */
function updateCacheAfterSubscription(
  cache: ApolloCache<{}>,
  contextEntity:
    | FollowMessageFragment
    | ContextNodeFragment
    | NodeViewFragment
    | GroupHubDetailsQuery['groupHub']
    | GroupHubDescendantsNodeFragment,
  isSubscribed: boolean
): boolean {
  const { conversation, depth } = contextEntity as ContextMessageFragment;
  updateFollowsCacheAfterSubscription(cache);

  if (conversation && depth === 0) {
    // entity is a topic message, only update the conversation object in cache
    const id = cache.identify(conversation);
    return cache.modify({
      id,
      fields: {
        isSubscribed() {
          return isSubscribed;
        }
      }
    });
  } else {
    // entity is either a node or a reply. subscriptionsCount only exists in nodes
    // but since cache.modify cannot write fields that do not already exist in the
    // cache, then we can use the same function for both types
    const id = cache.identify(contextEntity);
    return cache.modify({
      id,
      fields: {
        subscribed() {
          return isSubscribed;
        },
        userContext(previousContext) {
          return {
            ...previousContext,
            isSubscribed
          };
        },
        subscriptionsCount(previousCount, { readField }) {
          const userContext: Readonly<NodeUserContext> = readField('userContext');
          // Create mutation always return the subscription if successful, even if it was already subscribed.
          // In this last case, we do not alter the count.
          if (userContext?.isSubscribed && isSubscribed) {
            return previousCount;
          }
          // check for nullity as zero is falsy
          return previousCount != null
            ? isSubscribed
              ? previousCount + 1
              : previousCount - 1
            : previousCount;
        }
      }
    });
  }
}

export {
  canSubscribeToNode,
  canSubscribeToMessage,
  updateCacheAfterSubscription,
  updateFollowsCacheAfterSubscription
};
